import React, { useEffect, useId, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { X, ArrowLeft } from "lucide-react";
import {
    SurfaceLayerName,
    SurfaceLayerProvider,
    useActiveSurfaceLayer,
    useResolvedSurfaceLayer,
} from "ui/layers";
import { useBodyScrollLock } from "ui/useBodyScrollLock";

interface ModalProps {
    isOpen: boolean
    onClose: () => void
    title: string | React.ReactNode
    children: React.ReactNode
    className?: string
    overlayClassName?: string
    confirmationText?: string
    onConfirm?: () => void
    showConfirmation?: boolean
    onCancelConfirmation?: () => void
    displayStyle?: 'cover' | 'page' | 'modal'
    showBackButton?: boolean
    onBack?: () => void
    showSkip?: boolean
    titleId?: string;
    footer?: React.ReactNode;
    stickyFooter?: boolean;
    bodyDisplayStyle?: string;
    disableOutsideClose?: boolean;
    layerBase?: number;
    surfaceTier?: SurfaceLayerName;
    /**
     * Optional id of the `<form>` element rendered inside `children`.
     *
     * When set, the modal exposes the id as a `data-form-id` attribute on
     * the modal content surface so footer submit buttons rendered via
     * `FormActions` can pair to the form via `<button form={formId}>`.
     * This is what enables browser-native Enter-key submit when the
     * primary submit button lives in the modal footer (outside the form).
     *
     * Strictly informational at this layer -- the consumer is responsible
     * for ensuring `<form id={formId}>` exists somewhere in `children`.
     * See `docs/architecture/forms-standard.md`.
     */
    formId?: string;
}

/**
 * Modal
 *
 * A11y-friendly modal with optional sticky footer and close/back controls.
 * This component ensures the scrollable content area resets to the top
 * whenever the modal opens or its content/title change (e.g., stepper modals),
 * preventing users from landing mid-scroll between sections.
 */
const Modal: React.FC<ModalProps> = ({ 
    isOpen, 
    onClose, 
    title, 
    children, 
    className = '',
    overlayClassName = '',
    confirmationText,
    onConfirm,
    showConfirmation = false,
    onCancelConfirmation,
    displayStyle = 'modal',
    showBackButton = false,
    onBack,
    showSkip = false,
    titleId,
    footer,
    stickyFooter = true,
    bodyDisplayStyle = 'flex',
    disableOutsideClose = false,
    layerBase,
    surfaceTier = "modal",
    formId,
}) => {
    const parentLayer = useActiveSurfaceLayer();
    const surfaceId = useId();
    const surfaceRef = useRef<HTMLDivElement>(null);
    const [contentElement, setContentElement] = useState<HTMLDivElement | null>(null);
    // Ref to the scrollable body so we can imperatively reset its scroll position
    const modalBodyRef = useRef<HTMLDivElement>(null);
    const titleIdString = titleId || (typeof title === "string" ? title.replace(/\s+/g, "-").toLowerCase() : "modal-title");
    const [portalContainer, setPortalContainer] = useState<HTMLElement | null>(null);
    const layer = useResolvedSurfaceLayer({
        tier: surfaceTier,
        layerBase,
    });
    const providedLayer = useMemo(
        () => ({ ...layer, contentElement }),
        [contentElement, layer]
    );

    // Ensure a stable portal container at the document.body level
    useEffect(() => {
        if (typeof window === "undefined") {
            return;
        }

        let container: HTMLElement | null = null;
        const targetRoot = parentLayer?.contentElement ?? document.body;

        if (targetRoot === document.body) {
            container = document.getElementById("eoe-modal-root");
            if (!container) {
                const created = document.createElement("div");
                created.setAttribute("id", "eoe-modal-root");
                created.setAttribute("data-eoe-modal-root", "");
                document.body.appendChild(created);
                container = created;
            }
        } else {
            const created = document.createElement("div");
            created.setAttribute("data-eoe-modal-root", "");
            targetRoot.appendChild(created);
            container = created;
        }

        setPortalContainer(container);

        return () => {
            if (container && container.parentElement && container.id !== "eoe-modal-root") {
                container.parentElement.removeChild(container);
            }
        };
    }, [parentLayer]);

    useBodyScrollLock(isOpen);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
          if (!contentElement || disableOutsideClose) return;

          const target = event.target as HTMLElement;
          const clickedSurfaceId = target.closest("[data-eoe-surface-id]")?.getAttribute("data-eoe-surface-id");

          // Check if the click is on the floating feedback button or its drawer
          const isFloatingFeedbackButton = target.closest('[data-floating-feedback]') || 
                                         target.closest('[data-vaul-drawer]') ||
                                         target.closest('[data-vaul-overlay]') ||
                                         target.closest('[data-vaul-drag-handle]');

          // Additional guards for nested surfaces, Radix portals, and Tippy tooltips.
          const clickedInsideThisModal = contentElement.contains(target);
          const clickedInsideThisSurface = clickedSurfaceId === surfaceId;
          const clickedInsideAnotherSurface = Boolean(clickedSurfaceId && clickedSurfaceId !== surfaceId);
          const clickedInsideTippy = Boolean(target.closest('.tippy-box'));
          const clickedInsideRadixPortal = Boolean(
            target.closest("[data-radix-popper-content-wrapper]") ||
            target.closest("[data-radix-portal]") ||
            target.closest("[data-radix-trigger]")
          );
          const shouldKeepOpen = clickedInsideThisModal ||
            clickedInsideAnotherSurface ||
            clickedInsideTippy ||
            clickedInsideRadixPortal ||
            Boolean(isFloatingFeedbackButton);

          if (shouldKeepOpen) {
            return;
          }

          // Close when the pointer lands on this modal's backdrop or outside the
          // current surface tree entirely.
          if (
            clickedInsideThisSurface ||
            (!clickedSurfaceId && document.body.contains(event.target as Node))
          ) {
            event.stopPropagation();
            event.preventDefault();
            onClose();
          }
        };

        document.addEventListener("pointerdown", handleClickOutside);
        return () => {
          document.removeEventListener("pointerdown", handleClickOutside);
        };
      }, [contentElement, disableOutsideClose, onClose, surfaceId]);

    // Reset the modal body's scroll position to the top on open or when content changes.
    // This mimics a ScrollToTop behavior for the modal context, which is
    // especially important for multi-step flows such as WelcomeModal.
    useEffect(() => {
        if (!isOpen) {
            return;
        }
        const bodyEl = modalBodyRef.current;
        if (bodyEl) {
            bodyEl.scrollTop = 0;
            bodyEl.scrollLeft = 0;
        }
    }, [isOpen, children, title]);

    if (!isOpen || !portalContainer) return null;

    return createPortal(
        <>
            <div
                className={`fixed inset-0 bg-black bg-opacity-50 ${overlayClassName}`}
                style={{ zIndex: layer.overlay }}
                data-eoe-surface-root="true"
                data-eoe-surface-overlay="true"
                data-eoe-surface-type={surfaceTier}
                data-eoe-surface-id={surfaceId}
                tabIndex={-1}
            />
            <div
                ref={surfaceRef}
                className="fixed inset-0 flex items-center justify-center pointer-events-none"
                style={{ zIndex: layer.base }}
                data-eoe-surface-root="true"
                data-eoe-surface-type={surfaceTier}
                data-eoe-surface-id={surfaceId}
                data-eoe-layer-base={layer.base}
            >
                <div
                    className="modal-sheet pointer-events-auto flex h-full w-full items-center justify-center"
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby={`${titleIdString}-title`}
                    tabIndex={-1}
                >
                    <div
                        className={`modal-content relative flex h-full max-h-[100vh] w-full flex-col bg-eoe-bg px-4 sm:h-auto sm:max-h-[90vh] sm:max-w-2xl sm:rounded-2xl sm:px-6 dark:bg-eoe-dark-bg-modal ${className}`}
                        ref={setContentElement}
                        data-eoe-surface-content="true"
                        data-form-id={formId}
                    >
                        <SurfaceLayerProvider value={providedLayer}>
                            <div className={`modal-header flex items-center justify-between ${title ? 'mb-6' : 'mb-2'}`}>
                                {showBackButton && onBack && (
                                    <button 
                                        onClick={onBack}
                                        className="modal-back-button size-7 rounded-full text-eoe-text dark:text-eoe-dark-text hover:bg-eoe-icon-hover dark:hover:bg-eoe-dark-icon-hover"
                                        aria-label="Go back"
                                    >
                                        <span className="flex items-center justify-center">
                                            <span className="flex">
                                                <ArrowLeft className="size-6 stroke-1" />
                                            </span>
                                        </span>
                                    </button>
                                )}
                                {showSkip ? (
                                    <button
                                        className="modal-close-button right-6 top-6 text-sm font-semibold text-eoe-text-meta dark:text-eoe-dark-text-meta hover:text-eoe-text dark:hover:text-eoe-dark-text"
                                        onClick={onClose}
                                        aria-label="Skip verification"
                                    >
                                        Skip
                                    </button>
                                ) : (
                                    <button
                                        className={`${showBackButton ? '' : 'left-2 sm:left-auto' } modal-close-button size-7 rounded-full bg-eoe-icon dark:bg-eoe-dark-icon hover:bg-eoe-icon-hover dark:hover:bg-eoe-dark-icon-hover`}
                                        onClick={onClose}
                                        aria-label="Close Modal"
                                    >
                                        <span className="flex items-center justify-center">
                                            <span className="flex">
                                                <X className="size-6 stroke-1" />
                                            </span>
                                        </span>
                                    </button>
                                )}
                            </div>

                            {title && (
                                <div className={`modal-title items-center mb-2 pt-4 ${footer || stickyFooter ? '' : 'flex flex-grow'}`}>
                                    <h2 id={`${titleIdString}-title`} className="text-eoe-text-heading dark:text-eoe-dark-text-heading text-2xl font-semibold">
                                        {title}
                                    </h2>
                                </div>
                            )}
                            
                            {/* Scrollable Content */}
                            <div
                                ref={modalBodyRef}
                                className={`modal-body ${bodyDisplayStyle} flex-1 overflow-y-auto overflow-x-hidden text-eoe-text dark:text-eoe-dark-text ${footer && stickyFooter ? 'pb-0' : 'pb-1'}`}
                            >
                                {children}
                            </div>

                            {/* Sticky Footer */}
                            {footer && stickyFooter && (
                                <div className="modal-footer flex flex-col flex-shrink-0 justify-end bg-eoe-bg dark:bg-eoe-dark-bg-modal">
                                    {footer}
                                </div>
                            )}

                            {/* Non-sticky Footer (added to content flow) */}
                            {footer && !stickyFooter && footer}

                            {/* Confirmation Dialog */}
                            {showConfirmation && confirmationText && (
                                <div className="modal-dialog fixed inset-0 flex items-center justify-center bg-black bg-opacity-50" style={{ zIndex: layer.portal }}>
                                    <div className="modal-dialog-content mx-4 w-full max-w-sm rounded-lg bg-eoe-bg p-6 dark:bg-eoe-dark-bg-modal">
                                        <p className="mb-6 text-eoe-text dark:text-eoe-dark-text">{confirmationText}</p>
                                        <div className="flex justify-end space-x-2">
                                            <button 
                                                className="btn-secondary px-4 py-2" 
                                                onClick={onCancelConfirmation}
                                            >
                                                No
                                            </button>
                                            <button 
                                                className="btn-primary px-4 py-2" 
                                                onClick={onConfirm}
                                            >
                                                Yes
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </SurfaceLayerProvider>
                    </div>
                </div>
            </div>
        </>,
        portalContainer
    );
}

export default Modal;
